import { CategoryDTO } from '@/api/dto/CategoryDTO';
import {
  ICustomOption,
  IFieldFilter,
  ISearchFilter,
  ITableHeader,
} from '@/models/filters/Filters';
import { SearchQueryFilters } from '@/models/filters/SearchQueryFilters';
import { CrudModule, IModuleState } from '@/store/entityModules/CrudModule';
import { Category } from '@/models/entities/Category';
import { ActionTree, GetterTree } from 'vuex';
import { EntityType } from '@/store/entityModules/types';
import { getEntityApiClient } from '@/api/utils/GetClient';
import { AlertColor, chooseErrorMessageData, dispatchAlert } from '@/utils/alerts';

class CategoriesModule extends CrudModule<Category, CategoryDTO> {
  state: IModuleState<Category, CategoryDTO> = {
    ...this.state,

    entityType: EntityType.CATEGORY,
    queryFilters: new SearchQueryFilters({}),
    customFilteredItems: [],
    customLoading: false,
    customFilters: new SearchQueryFilters({ page: 1, rowsPerPage: 20 }),
  };

  getters: GetterTree<IModuleState<Category, CategoryDTO>, any> = {
    ...this.getters,

    getItem: state => (id: number) => {
      const item = state.items.find((i: Category) => i.id === id);
      return item || null;
    },

    customFilteredItems(state): Category[] | undefined {
      return state.customFilteredItems;
    },

    getSearchValue(state): string | null {
      const filter = state.queryFilters as SearchQueryFilters;
      if (!filter.searchFilter) return null;
      return filter.searchFilter.value || null;
    },

    getSearchHeaders(state): ITableHeader[] {
      const filter = state.queryFilters as SearchQueryFilters;
      if (!filter.searchFilter) return [];
      return filter.searchFilter.headers;
    },
  };

  actions: ActionTree<IModuleState<Category, CategoryDTO>, any> = {
    ...this.actions,

    setSearchValue({ getters, commit }, value: string | null) {
      commit('setQueryFilters', {
        searchFilter: {
          value,
          headers: getters['getSearchHeaders'],
        },
        // show results from the 1st page
        page: 1,
      });
    },

    setSearchHeaders({ commit, getters }, value: ITableHeader[]) {
      commit('setQueryFilters', {
        searchFilter: {
          headers: value,
        },
      });
    },

    setCustomFiltersNextPage({ state }) {
      if (state.customFilters) state.customFilters.page += 1;
    },

    setCustomFilterField({ state }, filterFields: IFieldFilter) {
      if (state.customFilters && filterFields) {
        state.customFilters.fieldFilter = filterFields;
        state.customFilters.page = 1;
      }
    },

    setCustomInitialValue({ state }, defaultValue: number[] | number) {
      if (defaultValue !== undefined && typeof defaultValue !== 'number')
        defaultValue = defaultValue?.filter(item => item !== undefined);

      if (state.customFilters) {
        if (
          defaultValue !== undefined &&
          (typeof defaultValue === 'number' || defaultValue.length > 0)
        ) {
          if (typeof defaultValue === 'number')
            state.customFilters.initialValue = [defaultValue];
          else state.customFilters.initialValue = defaultValue;
        } else state.customFilters.initialValue = undefined;
      }
    },

    setCustomSearchField({ state }, searchFields: ISearchFilter) {
      if (state.customFilters) {
        state.customFilters.searchFilter = searchFields;
        state.customFilters.page = 1;
      }
    },

    async fetchCustomFilteredItems(
      { state, commit, dispatch },
      {
        startFromPage,
        customOptions,
      }: { startFromPage?: number; customOptions?: ICustomOption[] } = {},
    ) {
      if (startFromPage && startFromPage === 1) state.customFilteredItems = [];

      if (state.customFilters && startFromPage) {
        state.customFilters.page = startFromPage;
      }

      commit('setCustomLoading', true);

      try {
        const apiClient = getEntityApiClient(state.entityType);
        const response = await apiClient.fetchItems(
          state.customFilters || new SearchQueryFilters({}),
          customOptions,
        );
        commit('setCustomFilteredItems', response.items);
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          const errorMessage = chooseErrorMessageData(
            {
              entityName: state.entityType,
              action: 'fetch',
            },
            err.message,
          );
          dispatchAlert(AlertColor.ERROR, errorMessage, dispatch);
        }
      } finally {
        commit('setCustomLoading', false);
      }
    },
  };
}

const categoriesModule = new CategoriesModule();
export default categoriesModule;
